// src/components/zoom/CreateZoomRoomModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiZap } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { createZoomRoom, listWorkspaceCourses, zoomErrorMessage } from '../../services/zoomApiService';

const CreateZoomRoomModal = ({ account, accounts, kind, workspaceId, onClose }) => {
  const [courses, setCourses] = useState(null);
  const [search, setSearch] = useState('');
  const [courseId, setCourseId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { listWorkspaceCourses(workspaceId).then(setCourses).catch(() => setCourses([])); }, [workspaceId]);

  const filtered = useMemo(() => (courses || []).filter(c => c.name.toLowerCase().includes(search.toLowerCase())), [courses, search]);
  const course = (courses || []).find(c => c.id === courseId);
  const isMC = kind === 'masterclass';

  const handleCreate = async () => {
    if (!course) return;
    setSaving(true);
    try {
      const r = await createZoomRoom({ zoomAccountDocId: account.id, courseId: course.id, courseName: course.name, kind });
      if (r.created) toast.success(`Sala creada en Zoom: ${r.room.name}`);
      else {
        const holder = r.accountId && r.accountId !== account.id ? (accounts || []).find(a => a.id === r.accountId) : null;
        const where = holder ? ` en la cuenta "${holder.name}"` : '';
        toast(`Ya existía una sala de ${isMC ? 'Masterclass' : 'Clase'} para este curso${where} (no se duplicó)`, { icon: 'ℹ️' });
      }
      onClose();
    } catch (err) {
      toast.error(zoomErrorMessage(err), { duration: 7000 });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><FiZap /> Crear sala de {isMC ? 'Masterclass' : 'Clase'} en Zoom</h3>
            <p className="text-xs text-gray-500 mt-0.5">{account.name} · {account.email}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"><FiX size={18} /></button>
        </div>
        <div className="p-5 space-y-3 flex-1 overflow-hidden flex flex-col">
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar curso…"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
          <div className="flex-1 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-50">
            {courses === null && <p className="p-4 text-sm text-gray-400 text-center">Cargando cursos…</p>}
            {courses && filtered.length === 0 && <p className="p-4 text-sm text-gray-400 text-center">Sin cursos</p>}
            {filtered.map(c => (
              <button key={c.id} onClick={() => setCourseId(c.id)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${c.id === courseId ? 'bg-blue-50 text-blue-700 font-semibold' : 'hover:bg-gray-50 text-gray-700'}`}>
                {c.name}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-400">Se crea con los Ajustes de creación (contraseña, grabación en la nube, etc.) y queda guardada en esta cuenta.</p>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
          <button onClick={handleCreate} disabled={!course || saving}
            className={`px-4 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50 ${isMC ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {saving ? 'Creando en Zoom…' : 'Crear sala'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateZoomRoomModal;
